import { PlusOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { useBasicDndDragger } from '@framework/ui/core/dnd/hooks/useBasicDndDragger.ts';
import { Button, Space } from 'antd';
import { useCallback, useState } from 'react';
import { parseAdvancedRichTextToString } from '../../../../../../validator/utils/parseAdvancedRichTextToString.ts';
import { type IWidgetOption } from '../../../../Widget.ts';
import { ImportCsvOptionsButton } from './ImportCsvOptionsButton.tsx';
import { SortableWidgetOptionItem } from './SortableWidgetOptionItem.tsx';
import { createDefaultOptionValue } from './util/createDefaultOptionValue.ts';
import { type IWidgetCsvConfig } from './WidgetCsvField.ts';
import { WidgetOptionDividerEditDrawer } from './WidgetOptionDividerEditDrawer.tsx';
import { WidgetOptionValueEditDrawer } from './WidgetOptionValueEditDrawer.tsx';
import { WidgetOptionView } from './WidgetOptionView.tsx';

export interface IWidgetOptionItemListProps {
  name: string;
  hideDividerDrawer?: boolean;
}

/**
 * Componente con D'n'D per la configurazione della lista delle opzioni di widget checkbox/radio/select
 */
export function WidgetOptionItemList(props: IWidgetOptionItemListProps) {
  const { name, hideDividerDrawer } = props;

  const editor = useRelationEditor<IWidgetOption>(name);
  const [index, setIndex] = useState<number | null>(null);
  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  const sortyByLabel = useCallback(() => {
    editor.replace(
      editor.items.sort((a, b) => {
        const aLabel =
          a.type === 'divider' ? '' : parseAdvancedRichTextToString(a.label);
        const bLabel =
          b.type === 'divider' ? '' : parseAdvancedRichTextToString(b.label);
        return aLabel.localeCompare(bLabel);
      }),
    );
  }, [editor]);

  const sortByValue = useCallback(() => {
    editor.replace(
      editor.items.sort((a, b) => {
        const aValue = a.type === 'divider' ? '' : a.value;
        const bValue = b.type === 'divider' ? '' : b.value;
        return aValue.localeCompare(bValue);
      }),
    );
  }, [editor]);

  // Funzione per gestire l'importazione delle opzioni
  const handleImport = (config: IWidgetCsvConfig) => {
    // Sostiuisce le opzioni attuali con quelle importate
    editor.replace(
      config.combinations.map(combination => ({
        type: 'option',
        label: [
          {
            text: combination.values[0],
            type: 'text',
            rule: null,
          },
        ],
        value: combination.values[1],
        visible: { type: 'static', value: true },
        enabled: { type: 'static', value: true },
      })),
    );
  };

  return (
    <>
      <Space>
        <Button onClick={sortyByLabel}>Ordina per Etichetta</Button>
        <Button onClick={sortByValue}>Ordina per Valore</Button>
      </Space>
      <FormItem name={name} label={null}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {editor.items.map((item, index) => (
              <SortableWidgetOptionItem
                item={item}
                uid={item.__uid!}
                type={item.type}
                key={item.__uid}
                active={active}
                onEdit={e => {
                  editor.handle.open(item);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onRemove={e => {
                  editor.remove(item);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onAdd={e => {
                  setIndex(index + 1);
                  editor.handle.open(createDefaultOptionValue());

                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            ))}
            <DragOverlay>
              {active && <WidgetOptionView item={active} />}
            </DragOverlay>
          </SortableContext>
        </DndContext>

        {/* Pulsanti da aggiunta */}
        <RowElement gutter={8}>
          <WidgetOptionValueEditDrawer
            editor={editor}
            index={index}
            setIndex={setIndex}
            buttonProps={{
              type: 'dashed',
              size: 'large',
              icon: <PlusOutlined aria-hidden="true" />,
              block: true,
              children: 'Aggiungi Opzione',
            }}
          />
          {!hideDividerDrawer && (
            <WidgetOptionDividerEditDrawer
              editor={editor}
              buttonProps={{
                type: 'dashed',
                size: 'large',
                icon: <PlusOutlined aria-hidden="true" />,
                block: true,
                children: 'Aggiungi Separatore',
              }}
            />
          )}
        </RowElement>

        {/* Pulsante per import da file csv */}
        <ImportCsvOptionsButton onImport={handleImport} />
      </FormItem>
    </>
  );
}
